import React from 'react';

import {
  FetchAllCities,
  FetchAllCountries,
  FetchAllStates,
  SearchLocations,
} from 'api/utils';

// Matches the server's own floor: a one-character prefix matches too much of
// the world to suggest anything useful.
export const MIN_SEARCH_CHARS = 2;
// Long enough that typing a place name start to finish asks for one search
// rather than one per keystroke.
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * The behaviour behind the one Location field, shared by investor onboarding
 * and profile editing (ported from startup-web's hook of the same name).
 *
 * An investor types a place and picks it, and whichever levels that place has
 * are stored - Bengaluru fills city, state and country; Karnataka fills state
 * and country; Singapore fills only a country. The country -> state -> city
 * cascade stays available behind `manualEntry` for places the master tables
 * are missing.
 */
const useLocationSearch = (formik) => {
  const [manualEntry, setManualEntry] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState('');
  const [searchText, setSearchText] = React.useState('');
  const [selected, setSelected] = React.useState(null);

  // Hold the request until typing settles, and never send a term the server
  // would only refuse.
  React.useEffect(() => {
    const term = searchInput.trim();
    if (term.length < MIN_SEARCH_CHARS) {
      setSearchText('');
      return undefined;
    }
    const timer = setTimeout(() => setSearchText(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: locationMatches, isFetching: isSearching } = SearchLocations({
    searchText,
  });

  // The hook holds the previous results while a new term is in flight, which
  // would otherwise keep offering the last matches after the box is cleared.
  const searchable = searchText.length >= MIN_SEARCH_CHARS;
  const matches = searchable ? locationMatches || [] : [];

  // The cascade's own lists. They serve manual entry, and they also resolve a
  // saved location back into a label when an investor returns to the screen.
  const { data: countries, isLoading: isLoadingCountries } =
    FetchAllCountries({});
  const savedCountry = (countries || []).find(
    (country) => country?._id === formik.values.country_id
  );
  const { data: states, isLoading: isLoadingStates } = FetchAllStates({
    countryID: savedCountry?.value,
  });
  const savedState = (states || []).find(
    (state) => state?._id === formik.values.state_id
  );
  const { data: cities, isLoading: isLoadingCities } = FetchAllCities({
    stateID: savedState?.value,
  });
  const savedCity = (cities || []).find(
    (city) => city?._id === formik.values.city_id
  );

  // Some countries have no states at all (Antarctica), and some states have no
  // cities. Hide the dropdown rather than showing an empty list the investor
  // cannot satisfy. Both stay visible while nothing is selected and while the
  // list is still loading, so they don't flicker away and back.
  const showStateField = !(
    formik.values.country_id &&
    !isLoadingStates &&
    (states || []).length === 0
  );
  const showCityField =
    showStateField &&
    !(formik.values.state_id && !isLoadingCities && (cities || []).length === 0);

  // One setValues, not three setFieldValue calls: each setFieldValue validates
  // against the values from the last render, so the trailing ones would
  // resurrect an error that was just cleared.
  // Errors are cleared too: onboarding validates only on Next, so a stale one would linger.
  const setLocation = ({ city_id = '', state_id = '', country_id = '' }) => {
    formik.setValues({ ...formik.values, city_id, state_id, country_id });
    ['city_id', 'state_id', 'country_id'].forEach((field) => {
      if (formik.errors?.[field]) formik.setFieldError(field, undefined);
    });
  };

  const selectOption = (option) => {
    setSelected(option);
    // Every level is rewritten, so picking a country after a city cannot leave
    // the finer ids behind.
    setLocation({
      city_id: option?.city?._id || '',
      state_id: option?.state?._id || '',
      country_id: option?.country?._id || '',
    });
  };

  const resetLocation = () => {
    setSelected(null);
    setSearchInput('');
    setSearchText('');
    setLocation({});
  };

  const switchMode = (toManual) => () => {
    resetLocation();
    setManualEntry(toManual);
  };

  // A saved location is shown at the precision it was stored at, so a
  // country-only answer reads "India" rather than ", , India".
  const savedLabel = [savedCity?.label, savedState?.label, savedCountry?.label]
    .filter(Boolean)
    .join(', ');

  return {
    // The one field
    matches,
    isSearching,
    searchable,
    searchInput,
    onSearchInput: setSearchInput,
    selected,
    selectOption,
    savedLabel,
    // Whichever of the two the screen's components want as a value
    selectedOption: selected || (savedLabel ? { label: savedLabel } : null),

    // Manual entry
    manualEntry,
    enterManual: switchMode(true),
    exitManual: switchMode(false),

    // The cascade behind it
    countries,
    states,
    cities,
    isLoadingCountries,
    isLoadingStates,
    isLoadingCities,
    savedCountry,
    savedState,
    savedCity,
    showStateField,
    showCityField,
    selectCountry: (option) =>
      setLocation({ country_id: option?._id || '' }),
    selectState: (option) =>
      setLocation({
        country_id: formik.values.country_id,
        state_id: option?._id || '',
      }),
    selectCity: (option) =>
      setLocation({
        country_id: formik.values.country_id,
        state_id: formik.values.state_id,
        city_id: option?._id || '',
      }),
  };
};

export default useLocationSearch;
